import React, { useState } from 'react';
import { TopBar } from '../components/TopBar';
import { Navbar } from '../components/Navbar';
import { RecentWorks } from '../components/RecentWorks';
import { useScreenInit } from '../useScreenInit';
import { motion, AnimatePresence } from 'framer-motion';
import { ArrowRight, Filter } from 'lucide-react';
import { Link } from 'react-router-dom';
const CATEGORIES = ['All', 'Weddings', 'Corporate', 'Social', 'Editorial'];
const INSPIRATION_ITEMS = [{
  title: 'Golden Hour Gala',
  category: 'Corporate',
  image: 'https://images.unsplash.com/photo-1511578314322-379afb476865?q=80&w=2069&auto=format&fit=crop',
  description: 'A sophisticated corporate gathering featuring warm lighting and premium rentals.'
}, {
  title: 'Ethereal Garden Wedding',
  category: 'Weddings',
  image: 'https://cdn.magicpatterns.com/uploads/9ctXptddB3hLoxqjhtMs1z/pexels-pavel-danilyuk-8815274.jpg',
  description: 'Romantic outdoor ceremony with soft florals and elegant seating.'
}, {
  title: 'Midnight Soirée',
  category: 'Social',
  image: 'https://cdn.magicpatterns.com/uploads/mr2X1sUaAGFTHwBbsAkqio/pexels-keynotez-12302950.jpg',
  description: 'An intimate evening celebration with atmospheric lighting and bespoke décor.'
}, {
  title: 'Modern Minimalist Editorial',
  category: 'Editorial',
  image: 'https://images.unsplash.com/photo-1519167758481-83f550bb49b3?q=80&w=2074&auto=format&fit=crop',
  description: 'Clean lines and a monochromatic palette for a high-fashion event concept.'
}, {
  title: 'Autumn Harvest Dinner',
  category: 'Social',
  image: 'https://images.unsplash.com/photo-1559339352-11d035aa65de?q=80&w=1974&auto=format&fit=crop',
  description: 'Warm tones and rustic elegance for a seasonal private dinner.'
}, {
  title: 'Luxe Bridal Shower',
  category: 'Weddings',
  image: 'https://images.unsplash.com/photo-1523292562811-8fa7962a78c8?q=80&w=2070&auto=format&fit=crop',
  description: 'A delicate and sophisticated celebration for the bride-to-be.'
}];
export function Inspiration() {
  useScreenInit();
  const [activeCategory, setActiveCategory] = useState('All');
  const filteredItems = activeCategory === 'All' ? INSPIRATION_ITEMS : INSPIRATION_ITEMS.filter(item => item.category === activeCategory);
  return <motion.div initial={{
    opacity: 0
  }} animate={{
    opacity: 1
  }} transition={{
    duration: 0.8
  }} className="min-h-screen bg-white font-sans selection:bg-[#b22222] selection:text-white" data-id="element-397">
      <TopBar data-id="element-398" />
      <Navbar data-id="element-399" />

      <main data-id="element-400">
        {/* Hero Section */}
        <section className="relative h-[60vh] flex items-center justify-center bg-black overflow-hidden" data-id="element-401">
          <div className="absolute inset-0 bg-cover bg-center opacity-60 scale-105" style={{
          backgroundImage: 'url("https://images.unsplash.com/photo-1519167758481-83f550bb49b3?q=80&w=2074&auto=format&fit=crop")'
        }} data-id="element-402" />
          <div className="relative text-center text-white z-10 px-6 max-w-4xl" data-id="element-403">
            <motion.span initial={{
            opacity: 0,
            y: 20
          }} animate={{
            opacity: 1,
            y: 0
          }} className="text-[10px] uppercase tracking-[0.4em] font-bold mb-6 block" data-id="element-404">
              A World of Elegance
            </motion.span>
            <motion.h1 initial={{
            opacity: 0,
            y: 20
          }} animate={{
            opacity: 1,
            y: 0
          }} transition={{
            delay: 0.2
          }} className="font-serif text-5xl md:text-7xl lg:text-8xl italic mb-8" data-id="element-405">
              Curated Inspiration
            </motion.h1>
            <motion.p initial={{
            opacity: 0,
            y: 20
          }} animate={{
            opacity: 1,
            y: 0
          }} transition={{
            delay: 0.4
          }} className="text-lg md:text-xl font-light tracking-wide leading-relaxed max-w-2xl mx-auto opacity-90" data-id="element-406">
              Discover a collection of our most breathtaking event concepts and
              design stories, crafted to spark your imagination.
            </motion.p>
          </div>
        </section>

        {/* Filter Section */}
        <section className="py-12 border-b border-gray-100 sticky top-20 lg:top-28 bg-white z-30" data-id="element-407">
          <div className="mx-4 md:mx-[4.1rem]" data-id="element-408">
            <div className="max-w-[1440px] mx-auto px-6 flex flex-col md:flex-row justify-between items-center gap-8" data-id="element-409">
              <div className="flex items-center gap-4 overflow-x-auto pb-4 md:pb-0 no-scrollbar w-full md:w-auto" data-id="element-410">
                <Filter size={16} className="text-gray-400 flex-shrink-0" data-id="element-411" />
                {CATEGORIES.map(cat => <button key={cat} onClick={() => setActiveCategory(cat)} className={`text-[11px] font-bold uppercase tracking-widest px-6 py-2 transition-all whitespace-nowrap ${activeCategory === cat ? 'bg-black text-white' : 'text-gray-500 hover:text-black'}`} data-id="element-412">
                    {cat}
                  </button>)}
              </div>
              <p className="text-[10px] uppercase tracking-widest font-bold text-gray-400" data-id="element-413">
                Showing {filteredItems.length} Concepts
              </p>
            </div>
          </div>
        </section>

        {/* Gallery Grid */}
        <section className="py-24" data-id="element-414">
          <div className="mx-4 md:mx-[4.1rem]" data-id="element-415">
            <div className="max-w-[1440px] mx-auto px-6" data-id="element-416">
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-12" data-id="element-417">
                <AnimatePresence mode="popLayout" data-id="element-418">
                  {filteredItems.map((item, index) => <motion.div key={item.title} layout initial={{
                  opacity: 0,
                  scale: 0.9
                }} animate={{
                  opacity: 1,
                  scale: 1
                }} exit={{
                  opacity: 0,
                  scale: 0.9
                }} transition={{
                  duration: 0.5,
                  delay: index * 0.1
                }} className="group cursor-pointer" data-id="element-419">
                      <div className="relative aspect-[4/5] overflow-hidden mb-8 bg-gray-50" data-id="element-420">
                        <img src={item.image} alt={item.title} className="w-full h-full object-cover transition-transform duration-1000 group-hover:scale-110" data-id="element-421" />
                        <div className="absolute inset-0 bg-black/0 group-hover:bg-black/20 transition-colors duration-500" data-id="element-422" />
                        <div className="absolute top-6 left-6" data-id="element-423">
                          <span className="bg-white text-black px-4 py-1.5 text-[9px] font-bold uppercase tracking-widest" data-id="element-424">
                            {item.category}
                          </span>
                        </div>
                      </div>
                      <div className="space-y-3" data-id="element-425">
                        <h3 className="font-serif text-2xl text-gray-900 group-hover:text-[#b22222] transition-colors" data-id="element-426">
                          {item.title}
                        </h3>
                        <p className="text-gray-500 text-sm leading-relaxed" data-id="element-427">
                          {item.description}
                        </p>
                        <div className="pt-4 flex items-center gap-2 text-[10px] font-bold uppercase tracking-widest text-black group-hover:text-[#b22222] transition-colors" data-id="element-428">
                          View Story <ArrowRight size={14} data-id="element-429" />
                        </div>
                      </div>
                    </motion.div>)}
                </AnimatePresence>
              </div>
            </div>
          </div>
        </section>

        {/* Moodboard Section */}
        <section className="py-24 bg-gray-50" data-id="element-430">
          <div className="mx-4 md:mx-[4.1rem]" data-id="element-431">
            <div className="max-w-[1440px] mx-auto px-6 grid grid-cols-1 lg:grid-cols-2 gap-20 items-center" data-id="element-432">
              <div className="space-y-8" data-id="element-433">
                <span className="text-[12px] uppercase tracking-[0.3em] font-bold text-[#b22222] block" data-id="element-434">
                  Design Philosophy
                </span>
                <h2 className="font-serif text-4xl md:text-5xl text-gray-900 leading-tight" data-id="element-435">
                  The Art of <br data-id="element-436" />
                  <span className="italic" data-id="element-437">Visual Harmony</span>
                </h2>
                <p className="text-gray-600 text-lg leading-relaxed" data-id="element-438">
                  We don't just provide rentals; we curate atmospheres. Every
                  inspiration story starts with a moodboard—a careful selection
                  of textures, colors, and materials that work together to
                  create a singular, unforgettable experience.
                </p>
                <div className="grid grid-cols-3 gap-4 pt-4" data-id="element-439">
                  <div className="aspect-square bg-[#b22222] shadow-lg" data-id="element-440" />
                  <div className="aspect-square bg-black shadow-lg" data-id="element-441" />
                  <div className="aspect-square bg-[#f5f5f5] shadow-lg" data-id="element-442" />
                </div>
              </div>
              <div className="relative aspect-square" data-id="element-443">
                <img src="https://images.unsplash.com/photo-1511795409834-ef04bbd61622?q=80&w=2069&auto=format&fit=crop" alt="Moodboard" className="w-full h-full object-cover shadow-2xl" data-id="element-444" />
                <div className="absolute -bottom-10 -left-10 w-48 h-48 bg-white p-8 shadow-xl hidden md:flex items-center justify-center text-center" data-id="element-445">
                  <p className="font-serif italic text-xl text-gray-900" data-id="element-446">
                    Curated <br data-id="element-447" /> Textures
                  </p>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Final CTA */}
        <section className="py-24 bg-black text-white text-center" data-id="element-448">
          <div className="mx-4 md:mx-[4.1rem]" data-id="element-449">
            <div className="max-w-4xl mx-auto px-6" data-id="element-450">
              <h2 className="font-serif text-4xl md:text-5xl mb-8 italic" data-id="element-451">
                Ready to Create Your Own Story?
              </h2>
              <p className="text-gray-400 text-lg mb-12 max-w-2xl mx-auto" data-id="element-452">
                Let's collaborate to bring your vision to life with our premium
                rentals and expert design team.
              </p>
              <Link to="/contact" data-id="element-453">
                <button className="bg-white text-black px-12 py-5 text-xs font-bold uppercase tracking-widest hover:bg-[#b22222] hover:text-white transition-all duration-300" data-id="element-454">
                  Book a Consultation
                </button>
              </Link>
            </div>
          </div>
        </section>
      </main>

      <footer className="bg-white py-12 border-t border-gray-100" data-id="element-455">
        <div className="mx-4 md:mx-[4.1rem]" data-id="element-456">
          <div className="max-w-[1440px] mx-auto px-6 flex flex-col md:flex-row justify-between items-center gap-8" data-id="element-457">
            <div className="flex flex-col items-center md:items-start leading-none" data-id="element-458">
              <span className="font-script text-3xl text-black" data-id="element-459">
                Chyne Designs
              </span>
              <span className="text-[8px] uppercase tracking-[0.2em] font-bold text-gray-400 mt-1" data-id="element-460">
                Elegant Events & Rentals LLC
              </span>
            </div>
            <div className="flex gap-8 text-[11px] font-bold uppercase tracking-widest text-gray-500" data-id="element-461">
              <a href="#" className="hover:text-black transition-colors" data-id="element-462">
                Privacy Policy
              </a>
              <a href="#" className="hover:text-black transition-colors" data-id="element-463">
                Terms of Service
              </a>
              <a href="#" className="hover:text-black transition-colors" data-id="element-464">
                Contact
              </a>
            </div>
            <p className="text-[11px] text-gray-400" data-id="element-465">
              © 2026 Chyne Designs Elegant Events & Rentals LLC. All rights
              reserved.
            </p>
          </div>
        </div>
      </footer>
    </motion.div>;
}